import { useState, useEffect, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Lock, Bell, KeyRound, Wrench } from "lucide-react";

function Section({ icon: Icon, title, desc, children }) {
  return (
    <div className="rounded-xl bg-fx-s1 border border-white/10 p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5 text-brandblue" />
        <div><h3 className="font-semibold">{title}</h3><p className="text-xs text-fx-s3">{desc}</p></div>
      </div>
      {children}
    </div>
  );
}

export default function AdminOps() {
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [notifs, setNotifs] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [corr, setCorr] = useState({ trade_id: "", field: "result", new_value: "", reason: "" });
  const [corrHistory, setCorrHistory] = useState([]);

  const load = useCallback(async () => {
    try { setNotifs((await api.get("/admin/settings/notifications")).data); } catch {}
    try { setLicenses((await api.get("/admin/licenses")).data || []); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);

  const changePw = async () => {
    try { await api.post("/auth/change-password", pw); toast.success("Password changed"); setPw({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const saveNotifs = async () => {
    try { await api.put("/admin/settings/notifications", notifs); toast.success("Notification settings saved"); }
    catch { toast.error("Failed to save"); }
  };
  const toggle = (grp, k) => setNotifs((n) => ({ ...n, [grp]: { ...n[grp], [k]: !n[grp][k] } }));

  const licAction = async (id, action, days = 30) => {
    try { await api.put(`/admin/licenses/${id}`, { action, days }); toast.success(`License ${action}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const loadCorr = async () => {
    if (!corr.trade_id) return;
    try { setCorrHistory((await api.get(`/admin/trades/${corr.trade_id}/corrections`)).data || []); } catch {}
  };
  const applyCorr = async () => {
    try {
      await api.post(`/admin/trades/${corr.trade_id}/correct`, { field: corr.field, new_value: corr.new_value, reason: corr.reason });
      toast.success("Correction recorded (original preserved)");
      setCorr({ ...corr, new_value: "", reason: "" });
      loadCorr();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="admin-ops">
      <Section icon={Lock} title="Change Password" desc="Update your administrator password. Requires 8+ chars with letters & numbers. Logged to the audit trail.">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1"><Label className="text-xs">Current password</Label><Input data-testid="pw-current" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="bg-fx-base border-white/10" /></div>
          <div className="space-y-1"><Label className="text-xs">New password</Label><Input data-testid="pw-new" type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="bg-fx-base border-white/10" /></div>
        </div>
        <Button data-testid="pw-save" onClick={changePw} className="bg-brandblue hover:bg-blue-500">Change password</Button>
      </Section>

      <Section icon={Bell} title="Notification Settings" desc="Choose which events trigger email notifications to admins and users.">
        {notifs ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {["admin_events", "user_events"].map((grp) => (
              <div key={grp}>
                <div className="text-xs font-bold uppercase tracking-wider text-fx-s3 mb-2">{grp === "admin_events" ? "Admin emails" : "User emails"}</div>
                <div className="space-y-1.5">
                  {Object.keys(notifs[grp]).map((k) => (
                    <label key={k} className="flex items-center justify-between gap-2 text-sm cursor-pointer">
                      <span className="capitalize">{k.replace(/_/g, " ")}</span>
                      <Switch data-testid={`notif-${grp}-${k}`} checked={!!notifs[grp][k]} onCheckedChange={() => toggle(grp, k)} />
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : <div className="text-fx-s3 text-sm">Loading…</div>}
        <Button data-testid="notif-save" onClick={saveNotifs} className="bg-brandblue hover:bg-blue-500">Save notification settings</Button>
      </Section>

      <Section icon={KeyRound} title="License Management" desc="Suspend, reactivate, extend or revoke bot licenses — even while a bot is running. All changes are audited.">
        {licenses.length === 0 ? <p className="text-sm text-fx-s3">No active licenses.</p> : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {licenses.map((l) => (
              <div key={l.id} data-testid={`license-${l.id}`} className="flex items-center justify-between gap-3 rounded-lg bg-fx-base border border-white/5 px-3 py-2">
                <div className="min-w-0 text-xs">
                  <div className="font-mono truncate">{l.license_key || l.id}</div>
                  <div className="text-fx-s3">status: {l.status || (l.licensed ? "active" : "inactive")}{l.expires_at ? " · exp " + new Date(l.expires_at).toLocaleDateString() : ""}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="outline" data-testid={`lic-suspend-${l.id}`} onClick={() => licAction(l.id, "suspend")} className="h-7 border-white/10 text-gold">Suspend</Button>
                  <Button size="sm" variant="outline" data-testid={`lic-activate-${l.id}`} onClick={() => licAction(l.id, "reactivate")} className="h-7 border-white/10 text-pos">Activate</Button>
                  <Button size="sm" variant="outline" data-testid={`lic-extend-${l.id}`} onClick={() => licAction(l.id, "extend", 30)} className="h-7 border-white/10">+30d</Button>
                  <Button size="sm" variant="outline" data-testid={`lic-revoke-${l.id}`} onClick={() => licAction(l.id, "revoke")} className="h-7 border-neg/40 text-neg">Revoke</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <Section icon={Wrench} title="Trade Corrections (audit-safe)" desc="Correct a genuine data-entry error. The original value is preserved and every correction is logged with your ID, time and reason.">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <Input data-testid="corr-trade-id" placeholder="Trade ID" value={corr.trade_id} onChange={(e) => setCorr({ ...corr, trade_id: e.target.value })} onBlur={loadCorr} className="bg-fx-base border-white/10 font-mono text-xs" />
          <select data-testid="corr-field" value={corr.field} onChange={(e) => setCorr({ ...corr, field: e.target.value })} className="h-9 rounded-md bg-fx-base border border-white/10 text-sm px-2 text-white">
            {["result", "pnl", "exit_price", "amount", "status"].map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
          <Input data-testid="corr-value" placeholder="New value" value={corr.new_value} onChange={(e) => setCorr({ ...corr, new_value: e.target.value })} className="bg-fx-base border-white/10" />
          <Input data-testid="corr-reason" placeholder="Reason (required)" value={corr.reason} onChange={(e) => setCorr({ ...corr, reason: e.target.value })} className="bg-fx-base border-white/10" />
        </div>
        <Button data-testid="corr-apply" onClick={applyCorr} className="bg-brandblue hover:bg-blue-500">Record correction</Button>
        {corrHistory.length > 0 && (
          <div className="text-xs space-y-1 border-t border-white/5 pt-2">
            <div className="text-fx-s3 uppercase tracking-wider">Correction history</div>
            {corrHistory.map((c) => (
              <div key={c.id} className="font-mono text-fx-s3">{c.field}: {String(c.original_value)} → {String(c.new_value)} · {c.reason} · {c.admin_email}</div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
